// markdown-renderer.js - Render markdown with syntax highlighting for chat messages

import MarkdownIt from 'markdown-it';
import DOMPurify from 'dompurify';

// Initialize markdown-it with options. Exportiert, damit der node-Test
// (markdown-renderer.test.js) dieselbe Instanz prüft — DOMPurify braucht
// ein Fenster, renderMarkdown selbst läuft in node nicht.
export const md = new MarkdownIt({
    html: false,         // Disable HTML tags in source
    breaks: false,       // Don't convert single line breaks to <br> (use standard markdown: 2 spaces at end or blank line)
    linkify: true,       // Auto-convert URLs to links
    typographer: true,   // Enable smart quotes and other typographic replacements
    highlight: function (str, lang) {
        // Simple syntax highlighting for code blocks
        if (lang) {
            return `<pre class="language-${lang}"><code class="language-${lang}">${escapeHtml(str)}</code></pre>`;
        }
        return `<pre><code>${escapeHtml(str)}</code></pre>`;
    }
});

// Nur Adressen mit Schema (http://, https://) verlinken. Ohne das hielt
// linkify Vault-Belege wie `Lungenembolie.md:12` für Web-Links auf die
// Moldau-TLD `.md`; processObsidianCitations griff dann nicht mehr und der
// Klick verließ die App.
md.linkify.set({ fuzzyLink: false });

// Helper function to escape HTML
function escapeHtml(text) {
    const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
}

/**
 * Render markdown text to safe HTML
 * @param {string} markdownText - The markdown text to render
 * @returns {string} - Sanitized HTML
 */
export function renderMarkdown(markdownText) {
    if (!markdownText) return '';

    // Pre-process text to fix common markdown issues
    // Ensure blank line before bullet lists (both - and *) to prevent markdown parsing issues
    let processedText = markdownText.replace(/([^\n])\n([-*]\s)/g, '$1\n\n$2');

    // Render markdown to HTML
    const rawHtml = md.render(processedText);

    // Sanitize HTML to prevent XSS attacks
    const cleanHtml = DOMPurify.sanitize(rawHtml, {
        ALLOWED_TAGS: [
            'p', 'br', 'strong', 'em', 'u', 's', 'code', 'pre',
            'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
            'ul', 'ol', 'li',
            'blockquote',
            'a',
            'table', 'thead', 'tbody', 'tr', 'th', 'td',
            'hr',
            'span', 'div'
        ],
        ALLOWED_ATTR: ['href', 'class', 'target', 'rel', 'data-command'],
        ALLOW_DATA_ATTR: true
    });

    return cleanHtml;
}

/**
 * Render inline markdown (without block elements)
 * @param {string} markdownText - The markdown text to render
 * @returns {string} - Sanitized inline HTML
 */
export function renderInlineMarkdown(markdownText) {
    if (!markdownText) return '';

    // Use renderInline for single-line content
    const rawHtml = md.renderInline(markdownText);

    // Sanitize HTML
    const cleanHtml = DOMPurify.sanitize(rawHtml, {
        ALLOWED_TAGS: ['strong', 'em', 'u', 's', 'code', 'a'],
        ALLOWED_ATTR: ['href', 'class', 'target', 'rel']
    });

    return cleanHtml;
}

export default { renderMarkdown, renderInlineMarkdown };
